import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft,
  Search, 
  Calendar,
  Trash2,
  User,
  Package,
  TrendingUp,
  Clock,
  MapPin
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOnHold } from '@/hooks/use-onhold';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { fetchLatestMarsPriceMapForSkus, getOnHoldUnitPrice } from '@/lib/mars-inventory-value';



export default function OnHoldReport() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [marsPriceMap, setMarsPriceMap] = useState<Map<string, number>>(() => new Map());
  
  // Hooks
  const { 
    onHoldItems, 
    loading, 
    fetchOnHoldItems, 
    deleteOnHoldItem 
  } = useOnHold();
  
  const { companyId } = useAuth();
  const { currentLocation, availableLocations, canSwitchLocation, setCurrentLocation } = useLocation();
  const { settings: profitSettings } = useProfitSettings();

  useEffect(() => {
    setSelectedLocation(currentLocation?.id || '');
  }, [currentLocation?.id]);

  const effectiveLocationId = selectedLocation || currentLocation?.id || null;

  // Load data on component mount and when filters change
  useEffect(() => {
    if (companyId) {
      fetchOnHoldItems({ 
        searchTerm, 
        dateFilter: dateFilter as any,
        companyId,
        locationId: effectiveLocationId || undefined,
      });
    }
  }, [searchTerm, dateFilter, companyId, effectiveLocationId, fetchOnHoldItems]);

  useEffect(() => {
    const skus = onHoldItems
      .map((i) => i.product?.item_code)
      .filter((c): c is string => Boolean(c));
    if (skus.length === 0) {
      setMarsPriceMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const map = await fetchLatestMarsPriceMapForSkus(skus, profitSettings.marsPriceTier);
      if (!cancelled) setMarsPriceMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [onHoldItems, profitSettings.marsPriceTier]);

  const filteredItems = onHoldItems;

  const handleDeleteItem = async (itemId: string) => {
    const success = await deleteOnHoldItem(itemId);
    if (success && companyId) {
      // Refresh the list
      fetchOnHoldItems({ 
        searchTerm, 
        dateFilter: dateFilter as any,
        companyId,
        locationId: effectiveLocationId || undefined,
      });
    }
  };

  const getStats = () => {
    const totalItems = onHoldItems.length;
    const totalValue = onHoldItems.reduce((sum, item) => {
      const unit = getOnHoldUnitPrice(item.product, marsPriceMap, item.hold_price);
      return sum + unit * (item.hold_quantity || 0);
    }, 0);
    const uniqueCustomers = new Set(onHoldItems.map(item => item.customer_id)).size;
    const oldItems = onHoldItems.filter(item => {
      const holdDate = new Date(item.date_on_hold);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - holdDate.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays > 7;
    }).length;

    return { totalItems, totalValue, uniqueCustomers, oldItems };
  };

  const { totalItems, totalValue, uniqueCustomers, oldItems } = getStats();

  const getDaysInHold = (holdDate: string) => {
    const hold = new Date(holdDate);
    const now = new Date();
    return Math.floor((now.getTime() - hold.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/onhold-sale')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Beklemedeki Satışlar
          </Button>
          
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Bekleme Raporu</h1>
            <p className="text-muted-foreground">
              Tüm beklemedeki ürünleri görüntüleyin ve yönetin
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Package className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{totalItems}</p>
                  <p className="text-sm text-muted-foreground">Toplam Ürün</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">₺{totalValue.toLocaleString('tr-TR')}</p>
                  <p className="text-sm text-muted-foreground">Toplam Değer</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <User className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{uniqueCustomers}</p>
                  <p className="text-sm text-muted-foreground">Müşteri</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Clock className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">{oldItems}</p>
                  <p className="text-sm text-muted-foreground">7+ Gün Eski</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Filtreler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="location">Lokasyon</Label>
                <Select
                  value={selectedLocation || ''}
                  onValueChange={(value) => {
                    setSelectedLocation(value);
                    if (canSwitchLocation) {
                      const nextLocation = availableLocations.find((loc) => loc.id === value);
                      if (nextLocation) {
                        setCurrentLocation(nextLocation);
                      }
                    }
                  }}
                  disabled={!canSwitchLocation}
                >
                  <SelectTrigger id="location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {location.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="search">Arama</Label>
                <Input
                  id="search"
                  placeholder="Ürün, müşteri veya telefon..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="dateFilter">Tarih Filtresi</Label>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger id="dateFilter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Zamanlar</SelectItem>
                    <SelectItem value="today">Bugün</SelectItem>
                    <SelectItem value="week">Son 7 Gün</SelectItem>
                    <SelectItem value="month">Son 30 Gün</SelectItem>
                    <SelectItem value="old">30+ Gün Eski</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Beklemedeki Ürünler ({filteredItems.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium mb-2">Ürün bulunamadı</h3>
                <p className="text-sm">
                  {searchTerm || dateFilter !== 'all' 
                    ? 'Filtre kriterlerinizi değiştirmeyi deneyin' 
                    : 'Henüz beklemedeki ürün bulunmuyor'
                  }
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ürün</TableHead>
                    <TableHead>Müşteri</TableHead>
                    <TableHead>Miktar</TableHead>
                    <TableHead>Fiyat</TableHead>
                    <TableHead>Toplam</TableHead>
                    <TableHead>Bekleme Süresi</TableHead>
                    <TableHead>Not</TableHead>
                    <TableHead>İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => {
                    const daysInHold = getDaysInHold(item.date_on_hold);
                    const customerName = `${item.customer?.first_name || ''} ${item.customer?.last_name || ''}`.trim();
                    const unitPrice = getOnHoldUnitPrice(item.product, marsPriceMap, item.hold_price);
                    const lineTotal = unitPrice * (item.hold_quantity || 0);
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.product?.name || 'Ürün bulunamadı'}</p>
                            <p className="text-sm text-muted-foreground">{item.product?.item_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{customerName || 'Müşteri bulunamadı'}</p>
                            <p className="text-sm text-muted-foreground">{item.customer?.phone || 'Telefon yok'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{item.hold_quantity} adet</TableCell>
                        <TableCell>₺{unitPrice.toLocaleString('tr-TR')}</TableCell>
                        <TableCell>₺{lineTotal.toLocaleString('tr-TR')}</TableCell>
                        <TableCell>
                          <Badge variant={daysInHold > 7 ? 'destructive' : daysInHold > 3 ? 'default' : 'secondary'}>
                            {daysInHold} gün
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">-</span>
                        </TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" disabled={loading}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Beklemeden Kaldır</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Bu ürünü beklemeden kaldırmak istediğinizden emin misiniz? 
                                  Bu işlem geri alınamaz.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>İptal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteItem(item.id)}>
                                  {loading ? 'Siliniyor...' : 'Sil'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}